/**
 * Sync Status Indicator Component
 * 
 * Displays in the header to show:
 * - Real-time sync status (idle, syncing, error)
 * - Last sync time
 * - Current bandwidth usage
 * - Network status indicator
 * - Quick access to bandwidth settings
 */

import { useState, useEffect } from 'react';
import { Activity, AlertCircle, CheckCircle2, Loader, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

// Constants
const TIME_AGO_UPDATE_INTERVAL_MS = 10000; // 10 seconds
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

export function SyncStatusIndicator() {
  const {
    isSyncing,
    lastSyncTime,
    syncError,
    bandwidthUsage,
    bandwidthSettings,
    triggerManualSync,
  } = useAuth();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showTooltip, setShowTooltip] = useState(false);
  const [timeAgo, setTimeAgo] = useState<string>('Never');

  // Update time ago every 10 seconds
  useEffect(() => {
    const updateTimeAgo = () => {
      if (!lastSyncTime) {
        setTimeAgo('Never');
        return;
      }

      const seconds = Math.floor((Date.now() - lastSyncTime.getTime()) / 1000);
      if (seconds < SECONDS_PER_MINUTE) {
        setTimeAgo('Just now');
      } else if (seconds < SECONDS_PER_HOUR) {
        setTimeAgo(`${Math.floor(seconds / SECONDS_PER_MINUTE)}m ago`);
      } else if (seconds < SECONDS_PER_DAY) {
        setTimeAgo(`${Math.floor(seconds / SECONDS_PER_HOUR)}h ago`);
      } else {
        setTimeAgo(`${Math.floor(seconds / SECONDS_PER_DAY)}d ago`);
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, TIME_AGO_UPDATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [lastSyncTime]);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getStatusColor = () => {
    if (syncError) return 'text-red-600';
    if (isSyncing) return 'text-blue-600';
    if (!isOnline) return 'text-gray-400';
    return 'text-green-600';
  };

  const getStatusIcon = () => {
    if (syncError) return <AlertCircle className="w-4 h-4 animate-pulse" />;
    if (isSyncing) return <Loader className="w-4 h-4 animate-spin" />;
    if (!isOnline) return <WifiOff className="w-4 h-4" />;
    return <CheckCircle2 className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (syncError) return 'Sync Error';
    if (isSyncing) return 'Syncing...';
    if (!isOnline) return 'Offline';
    return 'Synced';
  };

  const usagePercent = bandwidthUsage
    ? (bandwidthUsage.totalBytes / (bandwidthSettings?.dailyLimitMB || 25) / 1024 / 1024) * 100
    : 0;

  return (
    <div className="relative">
      {/* Status Button */}
      <button
        onClick={triggerManualSync}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={(e) => {
          // Only close if not moving to tooltip
          const relatedTarget = e.relatedTarget as HTMLElement;
          if (!relatedTarget?.closest('[data-tooltip]')) {
            setShowTooltip(false);
          }
        }}
        disabled={!isOnline || isSyncing}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all
          ${
            !isOnline || isSyncing
              ? 'bg-gray-100 cursor-not-allowed'
              : 'bg-gray-50 hover:bg-gray-100 active:bg-gray-200'
          }
        `}
        title={syncError ? syncError : 'Click to sync now'}
      >
        <div className={`flex items-center gap-1.5 ${getStatusColor()}`}>
          {getStatusIcon()}
          <span className="text-xs font-medium">{getStatusText()}</span>
        </div>

        {/* Network Indicator */}
        <div className={isOnline ? 'text-green-600' : 'text-gray-400'}>
          <Wifi className="w-3.5 h-3.5" />
        </div>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div 
          data-tooltip
          className="absolute right-0 top-full pt-2 z-50"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 min-w-72 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-gray-900">Sync Status</span>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                syncError
                  ? 'bg-red-100 text-red-700'
                  : isSyncing
                  ? 'bg-blue-100 text-blue-700'
                  : isOnline
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {isSyncing ? <Loader className="w-3 h-3 animate-spin mr-1" /> : null}
                {getStatusText()}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Status Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Sync</span>
                <span className="font-medium text-gray-900">{timeAgo}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Connection</span>
                <span className={`font-medium flex items-center gap-1 ${isOnline ? 'text-green-600' : 'text-gray-600'}`}>
                  {isOnline ? (
                    <>
                      <Wifi className="w-3 h-3" />
                      Online
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" />
                      Offline
                    </>
                  )}
                </span>
              </div>

              {bandwidthUsage && bandwidthSettings && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Bandwidth Used</span>
                    <span className="font-medium text-gray-900">
                      {(bandwidthUsage.totalBytes / 1024 / 1024).toFixed(1)} MB / {bandwidthSettings.dailyLimitMB} MB
                    </span>
                  </div>

                  {/* Usage Bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        usagePercent >= 95
                          ? 'bg-red-600'
                          : usagePercent >= 80
                          ? 'bg-yellow-600'
                          : 'bg-green-600'
                      }`}
                      style={{ width: `${Math.min(usagePercent, 100)}%` }}
                    ></div>
                  </div>

                  {/* Sync Priority */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Priority</span>
                    <span className="font-medium capitalize text-blue-600">
                      {bandwidthSettings.syncPriority}
                    </span>
                  </div>
                </>
              )}

              {/* At-Sea Mode */}
              {bandwidthSettings?.enableAtSeaMode && (
                <div className="flex items-center justify-between text-sm bg-amber-50 px-2 py-1.5 rounded border border-amber-200">
                  <span className="text-amber-800">🌊 At-Sea Mode</span>
                  <span className="text-xs font-medium text-amber-700">Active</span>
                </div>
              )}
            </div>

            {/* Error Alert */}
            {syncError && (
              <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm">
                <p className="text-red-700 font-medium flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Sync Error
                </p>
                <p className="text-red-600 text-xs mt-1">{syncError}</p>
              </div>
            )}

            {/* Warning for high usage */}
            {usagePercent >= 80 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-sm">
                <p className="text-yellow-700 font-medium">⚠️ High bandwidth usage</p>
                <p className="text-yellow-600 text-xs mt-1">
                  You've used {Math.round(usagePercent)}% of your daily limit
                </p>
              </div>
            )}

            {/* Info Message */}
            {isOnline && !isSyncing && !syncError && (
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm">
                <p className="text-blue-700 text-xs">
                  ✓ All systems operational. Tap icon to sync now.
                </p>
              </div>
            )}

            {!isOnline && (
              <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm">
                <p className="text-gray-700 text-xs">
                  📡 Working offline. Changes will sync when connection is restored.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
            <button
              onClick={() => {
                triggerManualSync();
                setShowTooltip(false);
              }}
              disabled={!isOnline || isSyncing}
              className={`w-full py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                isOnline && !isSyncing
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
